// Notemaker list: the note cards, in a gallery grid (landing) or a compact
// column (beside the editor). Pinned notes are grouped on top. Each card has
// a "⋯" button that opens its actions menu (pin, share, planner, archive,
// trash); a checkbox enables multi-select for the bulk bar.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Archive,
  ArchiveRestore,
  CalendarPlus,
  Check,
  Copy,
  MoreHorizontal,
  Pin,
  PinOff,
  RotateCcw,
  Trash2,
  Users,
} from "lucide-react";
import type { NoteScope, NoteSummary } from "../../api/notes";
import { relativeTime, swatchFor } from "./noteUtils";

export type NoteSort = "updated" | "created" | "title";

interface NoteActions {
  onOpen: (id: string) => void;
  onToggleCheck: (id: string) => void;
  onPin: (note: NoteSummary) => void;
  onArchive: (note: NoteSummary) => void;
  onTrash: (note: NoteSummary) => void;
  onRestore: (note: NoteSummary) => void;
  onDeleteForever: (note: NoteSummary) => void;
  onShare: (note: NoteSummary) => void;
  onPlanner: (note: NoteSummary) => void;
  onDuplicate: (note: NoteSummary) => void;
}

interface NoteCardProps extends NoteActions {
  note: NoteSummary;
  compact: boolean;
  selected: boolean;
  checked: boolean;
  selecting: boolean;
  scope: NoteScope;
}

// The menu is portalled to <body>: the card clips its overflow (colour
// stripe) and framer-motion's layout transform would otherwise trap a
// position:absolute popover inside it.
function CardMenu({
  anchor, onClose, children,
}: { anchor: HTMLElement; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const w = ref.current?.offsetWidth || 220;
      const h = ref.current?.offsetHeight || 240;
      const left = Math.max(8, Math.min(a.right - w, window.innerWidth - w - 8));
      const below = a.bottom + 6;
      const top = below + h > window.innerHeight - 8 ? Math.max(8, a.top - h - 6) : below;
      setPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      className="nm-card-menu"
      role="menu"
      aria-label="Card actions"
      style={pos ? { top: pos.top, left: pos.left } : { visibility: "hidden" }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}

function NoteCard({
  note, compact, selected, checked, selecting, scope,
  onOpen, onToggleCheck, onPin, onArchive, onTrash, onRestore, onDeleteForever, onShare, onPlanner, onDuplicate,
}: NoteCardProps) {
  const swatch = swatchFor(note.color);
  const trashed = scope === "trash";
  const sharedWithMe = note.is_shared;
  const extraTags = Math.max(0, note.tags.length - 3);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const stop = (e: React.MouseEvent, fn: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    fn();
  };

  const item = (label: string, icon: React.ReactNode, action: () => void, opts: { danger?: boolean } = {}) => (
    <button
      type="button"
      role="menuitem"
      className={`nm-menu-item${opts.danger ? " is-danger" : ""}`}
      onClick={(e) => stop(e, () => { setMenuOpen(false); action(); })}
    >
      {icon} {label}
    </button>
  );

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.18 }}
      className={`nm-card has-check${compact ? " is-compact" : ""}${selected ? " is-selected" : ""}${checked ? " is-checked" : ""}${menuOpen ? " is-menu-open" : ""}`}
      style={{ ["--nm-tint" as any]: swatch.tint, ["--nm-stripe" as any]: swatch.stripe }}
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      aria-label={`${note.title || "Untitled note"}${note.is_pinned ? ", pinned" : ""}${sharedWithMe ? `, shared by ${note.owner_name || "a colleague"}` : ""}`}
      data-testid={`note-card-${note.id}`}
      onClick={() => (selecting ? onToggleCheck(note.id) : onOpen(note.id))}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(note.id);
        }
      }}
    >
      {!sharedWithMe && (
        <button
          type="button"
          className={`nm-card-check${checked ? " is-on" : ""}`}
          aria-label={checked ? "Deselect note" : "Select note"}
          aria-pressed={checked}
          onClick={(e) => stop(e, () => onToggleCheck(note.id))}
        >
          {checked && <Check size={12} strokeWidth={3} aria-hidden="true" />}
        </button>
      )}

      <div className="nm-card-top">
        <span className={`nm-card-title${note.title ? "" : " is-untitled"}`}>{note.title || "Untitled note"}</span>
        {note.is_pinned && !trashed && !sharedWithMe && <Pin size={14} className="nm-card-pin" aria-label="Pinned" />}
        {!sharedWithMe && note.shared_with.length > 0 && (
          <span className="nm-card-pin" title={`Shared with ${note.shared_with.map((s) => s.name).join(", ")}`} aria-label={`Shared with ${note.shared_with.length}`}>
            <Users size={14} aria-hidden="true" />
          </span>
        )}
        <button
          ref={menuBtnRef}
          type="button"
          className="nm-card-more"
          aria-label={`Actions for ${note.title || "Untitled note"}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => stop(e, () => setMenuOpen((o) => !o))}
        >
          <MoreHorizontal size={16} aria-hidden="true" />
        </button>
      </div>
      {sharedWithMe && (
        <span className="nm-card-owner">
          <Users size={11} aria-hidden="true" /> Shared by {note.owner_name || "a colleague"}
        </span>
      )}
      <p className={`nm-card-snippet${note.snippet ? "" : " is-empty"}`}>{note.snippet || "No additional text"}</p>
      <div className="nm-card-foot">
        {note.tags.slice(0, 3).map((t) => <span key={t} className="nm-mini-tag">#{t}</span>)}
        {extraTags > 0 && <span className="nm-mini-tag">+{extraTags}</span>}
        <span className="nm-card-time" title={note.updated_at || undefined}>
          {trashed ? `Deleted ${relativeTime(note.deleted_at)}` : relativeTime(note.updated_at)}
          {/* Name the editor when it isn't simply the owner of my own note. */}
          {!trashed && note.last_edited_by_name && (sharedWithMe || note.last_edited_by !== note.owner_id)
            ? ` · ${note.last_edited_by_name}` : ""}
        </span>
      </div>

      {menuOpen && menuBtnRef.current && (
        <CardMenu anchor={menuBtnRef.current} onClose={() => setMenuOpen(false)}>
          {trashed ? (
            <>
              {item("Restore", <RotateCcw size={15} aria-hidden="true" />, () => onRestore(note))}
              {item("Delete forever", <Trash2 size={15} aria-hidden="true" />, () => onDeleteForever(note), { danger: true })}
            </>
          ) : sharedWithMe ? (
            <>
              {item("Send to Daily Planner", <CalendarPlus size={15} aria-hidden="true" />, () => onPlanner(note))}
              {item("Make my own copy", <Copy size={15} aria-hidden="true" />, () => onDuplicate(note))}
            </>
          ) : (
            <>
              {!note.is_archived && item(
                note.is_pinned ? "Unpin" : "Pin",
                note.is_pinned ? <PinOff size={15} aria-hidden="true" /> : <Pin size={15} aria-hidden="true" />,
                () => onPin(note)
              )}
              {item(
                note.shared_with.length ? `Share… (${note.shared_with.length})` : "Share…",
                <Users size={15} aria-hidden="true" />,
                () => onShare(note)
              )}
              {item("Send to Daily Planner", <CalendarPlus size={15} aria-hidden="true" />, () => onPlanner(note))}
              <div className="nm-menu-sep" />
              {item(
                note.is_archived ? "Unarchive" : "Archive",
                note.is_archived ? <ArchiveRestore size={15} aria-hidden="true" /> : <Archive size={15} aria-hidden="true" />,
                () => onArchive(note)
              )}
              {item("Move to Trash", <Trash2 size={15} aria-hidden="true" />, () => onTrash(note), { danger: true })}
            </>
          )}
        </CardMenu>
      )}
    </motion.div>
  );
}

interface NoteListProps extends NoteActions {
  notes: NoteSummary[];
  scope: NoteScope;
  variant: "grid" | "column";
  selectedId: string | null;
  checkedIds: Set<string>;
}

export default function NoteList({
  notes, scope, variant, selectedId, checkedIds, ...actions
}: NoteListProps) {
  const compact = variant === "column";
  const selecting = checkedIds.size > 0;
  const showGroups = scope === "all";
  const pinned = showGroups ? notes.filter((n) => n.is_pinned) : [];
  const others = showGroups ? notes.filter((n) => !n.is_pinned) : notes;

  const card = (note: NoteSummary) => (
    <NoteCard
      key={note.id}
      note={note}
      compact={compact}
      scope={scope}
      selected={note.id === selectedId}
      checked={checkedIds.has(note.id)}
      selecting={selecting}
      {...actions}
    />
  );

  return (
    <div className={compact ? "nm-column" : "nm-grid"} data-testid="note-list">
      <AnimatePresence initial={false}>
        {pinned.length > 0 && (
          <div className="nm-group-label" key="label-pinned">
            <span className="gt-eyebrow">Pinned</span>
          </div>
        )}
        {pinned.map(card)}
        {pinned.length > 0 && others.length > 0 && (
          <div className="nm-group-label" key="label-others">
            <span className="gt-eyebrow">Others</span>
          </div>
        )}
        {others.map(card)}
      </AnimatePresence>
    </div>
  );
}
